/**
 * ============================================================================
 * useCourtSchedules - Smart polling hook for today's court schedules
 * ============================================================================
 *
 * Features:
 * - Fetches schedule data for multiple courts in parallel
 * - Smart refetch intervals based on venue operational status
 * - Automatic invalidation when courts are added/removed
 * - Optimized for performance (no N+1 queries)
 *
 * ============================================================================
 */

import { useQuery } from '@tanstack/react-query';
import { vendorApi } from '../services/api';

interface Court {
  id: number;
  name: string;
  sport_id: number;
  sport?: {
    id: number;
    name: string;
  };
}

interface CourtSchedule {
  total_slots: number;
  available: number;
  booked: number;
  blocked: number;
  unavailable?: number;
  slots: Array<{
    time: string;
    start_time: string;
    end_time: string;
    status: 'available' | 'booked' | 'blocked' | 'unavailable';
    reference?: string | null;
    reason?: string | null;
    block_id?: number | null;
  }>;
}

interface VenueOperatingHours {
  is_open: boolean;
  opening_time?: string;
  closing_time?: string;
}

/**
 * Hook to fetch today's schedules for all courts
 */
export function useCourtSchedules(
  venueId: number | undefined,
  courts: Court[],
  operatingHours?: VenueOperatingHours
) {
  return useQuery({
    queryKey: ['court-schedules', venueId, courts.map(c => c.id).join(',')],
    queryFn: async () => {
      if (!venueId || courts.length === 0) {
        return {};
      }

      // Fetch all court schedules in parallel
      const schedulePromises = courts.map(async (court) => {
        try {
          const data = await vendorApi.getCourtTodaySchedule(venueId, court.id);
          return {
            courtId: court.id,
            schedule: data.schedule,
          };
        } catch (error) {
          console.error(`Failed to fetch schedule for court ${court.id}:`, error);
          return {
            courtId: court.id,
            schedule: null,
          };
        }
      });

      const results = await Promise.all(schedulePromises);

      // Convert array to object for easy lookup
      const scheduleMap: Record<number, CourtSchedule | null> = {};
      results.forEach(result => {
        scheduleMap[result.courtId] = result.schedule;
      });

      return scheduleMap;
    },
    enabled: !!venueId && courts.length > 0,

    // ============================================================================
    // SMART POLLING STRATEGY
    // ============================================================================
    refetchInterval: (query) => {
      const data = query.state.data;

      // If no data yet, retry quickly
      if (!data) return 30000; // 30 seconds

      // Get current hour (0-23)
      const currentHour = new Date().getHours();

      // ============================================================================
      // PRIORITY 1: Venue closed - Slow polling (5 minutes)
      // ============================================================================
      // When venue is closed, bookings can't be made, so we don't need frequent updates
      if (operatingHours && !operatingHours.is_open) {
        return 5 * 60 * 1000; // 5 minutes
      }

      // ============================================================================
      // PRIORITY 2: Off-peak hours (midnight - 6am) - Moderate polling (2 minutes)
      // ============================================================================
      // Very few people book courts at night, reduce server load
      if (currentHour >= 0 && currentHour < 6) {
        return 2 * 60 * 1000; // 2 minutes
      }

      // ============================================================================
      // PRIORITY 3: Peak hours (6am - midnight) - Fast polling (30 seconds)
      // ============================================================================
      // During business hours, bookings come in frequently
      // Vendors want real-time visibility of court availability
      return 30 * 1000; // 30 seconds
    },

    // Keep showing old data while refetching (prevents loading states)
    placeholderData: (previousData) => previousData,

    // Cache for 30 seconds (same as fastest refetch interval)
    staleTime: 30 * 1000,

    // Keep in cache for 5 minutes
    gcTime: 5 * 60 * 1000,
  });
}
